import React, { useState, useEffect, useRef } from "react";
import { TextField, Alert, AlertTitle } from "@mui/material";
import { useTranslation } from "react-i18next";
import { createNote, updateNote } from "../../services/noteService";
import { uploadAttachment } from "../../services/attachmentService";
import { Attachment } from "../../types";
import FileUploader from "../Common/FileUploader";
import FormDialog from "../Common/FormDialog";
import { extractErrorMessage } from "../../utils/apiHelpers";
import DraftPromptDialog from "../Common/DraftPromptDialog";
import {
  buildDraftKey,
  clearDraft,
  loadDraft,
  saveDraft,
} from "../../utils/draftStorage";

interface NoteFormProps {
  open: boolean;
  parentId: string;
  note?: { id: string; post: string } | null;
  onClose: () => void;
  onCreated: () => Promise<void>;
  onUpdated?: () => Promise<void>;
}

const NoteForm: React.FC<NoteFormProps> = ({
  open,
  parentId,
  note = null,
  onClose,
  onCreated,
  onUpdated,
}) => {
  const { t } = useTranslation();
  const [post, setPost] = useState("");
  const [error, setError] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [draftPromptOpen, setDraftPromptOpen] = useState(false);
  const [draftToRestore, setDraftToRestore] = useState<{ post: string } | null>(
    null,
  );
  const draftPromptShownRef = useRef(false);
  const skipDraftSaveRef = useRef(false);

  const draftKey = buildDraftKey("note", parentId);

  useEffect(() => {
    if (selectedFile && selectedFile.type.startsWith("image/")) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [selectedFile]);

  useEffect(() => {
    if (open) {
      setPost(note?.post || "");
      setError("");
      setAlert(null);
      if (!note) {
        setSelectedFile(null);
        setFileName("");
        setPreviewUrl(null);
      }
    }
  }, [open, note]);

  useEffect(() => {
    if (!open || note) {
      draftPromptShownRef.current = false;
      setDraftPromptOpen(false);
      setDraftToRestore(null);
      return;
    }
    skipDraftSaveRef.current = false;
    if (draftPromptShownRef.current) return;
    const run = async () => {
      const draft = await loadDraft<typeof draftToRestore>(draftKey);
      if (!draft?.data) return;

      const hasContent = !!draft.data.post;
      if (!hasContent) {
        await clearDraft(draftKey);
        return;
      }

      setDraftToRestore(draft.data);
      setDraftPromptOpen(true);
      draftPromptShownRef.current = true;
    };
    void run();
  }, [open, note, draftKey]);

  useEffect(() => {
    if (!open || note) return;
    if (skipDraftSaveRef.current) return;
    if (draftPromptOpen) return;
    const shouldSave = !!post.trim();

    if (!shouldSave) {
      clearDraft(draftKey);
      return;
    }

    saveDraft(draftKey, { post });
  }, [open, note, post, draftKey, draftPromptOpen]);

  const handleUseDraft = () => {
    if (!draftToRestore) return;
    setPost(draftToRestore.post || "");
    setDraftPromptOpen(false);
  };

  const handleStartNew = () => {
    clearDraft(draftKey);
    localStorage.removeItem("pendingNoteAttachments");
    setPost("");
    setSelectedFile(null);
    setFileName("");
    setPreviewUrl(null);
    setDraftPromptOpen(false);
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setFileName(file.name);
    setAlert(null);
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
    setFileName("");
    setPreviewUrl(null);
  };

  const handleSubmit = async () => {
    if (!post.trim()) {
      setError(t("notes.emptyNoteError") || "Нельзя добавить пустую заметку");
      return;
    }

    setUploading(true);
    setAlert(null);
    setError("");
    try {
      if (note) {
        await updateNote(note.id, post.trim());
        await onUpdated?.();
        setAlert({
          type: "success",
          message: t("notes.updateSuccess"),
        });
        return;
      }

      let attachmentId: string | null = null;
      if (selectedFile) {
        const uploadedAttachment = await uploadAttachment(parentId, selectedFile);
        attachmentId = uploadedAttachment.id;

        const newAttachment: Attachment = {
          id: uploadedAttachment.id,
          name: fileName,
          url: uploadedAttachment.url,
          type: uploadedAttachment.type,
          size: uploadedAttachment.size,
          parentType: "Task",
          role: "Attachment",
        };
        const currentAttachments = JSON.parse(
          localStorage.getItem("pendingNoteAttachments") || "[]"
        );
        localStorage.setItem(
          "pendingNoteAttachments",
          JSON.stringify([...currentAttachments, newAttachment])
        );
      }

      const noteData = {
        parentId,
        parentType: "Task",
        post: post.trim(),
        type: "Post",
        isInternal: false,
        ...(attachmentId && {
          attachmentsIds: [attachmentId],
          attachmentsNames: { [attachmentId]: fileName },
          attachmentsTypes: { [attachmentId]: selectedFile?.type || "" },
        }),
      };

      await createNote(noteData);
      setPost("");
      setSelectedFile(null);
      setFileName("");
      setPreviewUrl(null);
      clearDraft(draftKey);
      await onCreated();
      setAlert({
        type: "success",
        message: t("notes.createSuccess"),
      });
    } catch (err: any) {
      console.error("Ошибка при сохранении заметки:", err);
      const message = extractErrorMessage(
        err,
        note
          ? t("notes.updateError") || "Не удалось обновить заметку"
          : t("notes.createError") || "Не удалось сохранить заметку"
      );
      setError(message);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    skipDraftSaveRef.current = true;
    setPost("");
    setError("");
    setSelectedFile(null);
    setFileName("");
    setPreviewUrl(null);
    setAlert(null);
    onClose();
  };

  return (
    <>
      <FormDialog
      open={open}
      title={
        note
          ? t("notes.editNote") || "Редактировать заметку"
          : t("notes.newNote") || "Новая заметка"
      }
      onClose={handleClose}
      onSubmit={handleSubmit}
      submitLabel={
        note ? t("common.save") || "Сохранить" : t("notes.add") || "Добавить"
      }
      cancelLabel={t("common.cancel") || "Отмена"}
      loading={uploading}
      disabled={uploading || !post.trim()}
    >
      {alert && (
        <Alert
          severity={alert.type}
          onClose={() => setAlert(null)}
          sx={{ mb: 2 }}
        >
          <AlertTitle>
            {alert.type === "success"
              ? t("common.success") || "Успех"
              : t("common.error") || "Ошибка"}
          </AlertTitle>
          {alert.message}
        </Alert>
      )}
      <TextField
        autoFocus
        label={t("notes.textLabel") || "Текст заметки"}
        value={post}
        onChange={(e) => setPost(e.target.value)}
        multiline
        rows={4}
        fullWidth
        variant="outlined"
        error={!!error}
        helperText={error}
        disabled={uploading}
        sx={{ mb: 2 }}
      />
      {!note && (
        <FileUploader
          selectedFile={selectedFile}
          fileName={fileName}
          uploading={uploading}
          previewUrl={previewUrl}
          onFileSelect={handleFileSelect}
          onFileRemove={handleFileRemove}
        />
      )}
      </FormDialog>
      <DraftPromptDialog
        open={draftPromptOpen}
        onUseDraft={handleUseDraft}
        onStartNew={handleStartNew}
      />
    </>
  );
};

export default NoteForm;
